import { useEffect, useState } from 'react';
import { useApi, type CleaningPhotoResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';

interface Props {
  taskId: string;
  photo: CleaningPhotoResponse;
  /** Was auf dem Bild zu sehen ist — der Aufrufer kennt den Raum, die Kachel nicht. */
  label?: string;
  className?: string;
}

/**
 * Vorschau eines Reinigungsfotos. Holt das Bild als Blob über den authentifizierten Client und
 * zeigt es über eine Objekt-URL; die URL wird beim Abbauen wieder freigegeben.
 */
export function PhotoThumbnail({ taskId, photo, label, className }: Props) {
  const api = useApi();
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  // `photo.kind` ist ein Speicherwert („BathroomAfter"). Als Alternativtext beantwortet er die
  // Frage nicht, was auf dem Bild ist — der Aufrufer liefert den Raumnamen.
  const description = label ?? 'Foto der Reinigung';

  useEffect(() => {
    if (!photo.contentType.startsWith('image/')) {
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    api.cleaning
      .downloadPhoto(taskId, photo.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api.cleaning, taskId, photo.id, photo.contentType]);

  if (failed) {
    return (
      <div
        role="img"
        aria-label={`${description} — konnte nicht geladen werden`}
        className={cn(
          'border-line text-sealwax flex h-24 w-24 items-center justify-center rounded border p-1 text-center text-xs',
          className,
        )}
      >
        Nicht geladen
      </div>
    );
  }
  if (!photo.contentType.startsWith('image/')) {
    return (
      <div
        role="img"
        aria-label={`${description} — als PDF hinterlegt`}
        className={cn(
          'border-line text-ink-soft flex h-24 w-24 items-center justify-center rounded border text-xs',
          className,
        )}
      >
        PDF
      </div>
    );
  }
  if (!src) {
    // Die Ladefläche hing an einem Alias auf `--color-bg-mute`, das keine Tokenquelle
    // definiert — Tailwind hat die Klasse verworfen und die Fläche blieb unsichtbar.
    return (
      <div
        role="status"
        aria-label={`${description} wird geladen`}
        className={cn('bg-card-alt h-24 w-24 animate-pulse rounded', className)}
      />
    );
  }
  return (
    <img
      src={src}
      alt={description}
      className={cn('border-line h-24 w-24 rounded border object-cover', className)}
    />
  );
}
